// Updated infinite-scroll.js with proper width handling

class InfiniteScroll extends HTMLElement {
  constructor() {
    super();
    this.reviewsSectionInserted = false;
    this.initialProductCount = parseInt(this.dataset.initialProducts || "8");
    this.addEventListener("click", this.onClickHandler.bind(this));

    if (this.dataset.trigger == "infinite") {
      new IntersectionObserver(this.handleIntersection.bind(this), {
        rootMargin: "0px 0px 200px 0px",
      }).observe(this);
    }
  }

  onClickHandler() {
    if (
      this.classList.contains("loading") ||
      this.classList.contains("disabled")
    )
      return;
    this.classList.add("loading");
    this.classList.add("disabled");

    const sections = InfiniteScroll.getSections();
    sections.forEach(() => {
      const url = this.dataset.url;
      InfiniteScroll.renderSectionFromFetch(url, this);
    });
  }

  handleIntersection(entries, observer) {
    if (!entries[0].isIntersecting) return;
    observer.unobserve(this);
    this.onClickHandler();
  }

  static getSections() {
    return [
      {
        section: document.getElementById("product-grid").dataset.id,
      },
    ];
  }

  static renderSectionFromFetch(url, infiniteScrollElement) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        InfiniteScroll.renderPagination(html);
        InfiniteScroll.renderProductGridContainer(html, infiniteScrollElement);
      })
      .catch((e) => {
        console.error(e);
      });
  }

  static renderPagination(html) {
    const container = document
      .getElementById("ProductGridContainer")
      .querySelector(".pagination-wrapper");
    const pagination = new DOMParser()
      .parseFromString(html, "text/html")
      .getElementById("ProductGridContainer")
      .querySelector(".pagination-wrapper");

    if (pagination) {
      container.innerHTML = pagination.innerHTML;
    } else {
      container.remove();
    }
  }

  static renderProductGridContainer(html, infiniteScrollElement) {
    const container = document.getElementById("product-grid");
    const products = new DOMParser()
      .parseFromString(html, "text/html")
      .getElementById("product-grid");

    container.insertAdjacentHTML("beforeend", products.innerHTML);
  }

  static insertReviewsSection(container, afterIndex) {
    const productItems = container.querySelectorAll('.grid__item');
    if (productItems.length < afterIndex) return;

    // Hard guard — never insert twice
    if (container.querySelector('.reviews-section-wrapper')) return;

    const templateHeader = document.getElementById('reviews-header-template')
    const template = document.getElementById('reviews-section-template');
    if (!template) return;

    // Get grid column count from computed styles
    const gridStyle = window.getComputedStyle(container);
    const gridTemplateColumns = gridStyle.getPropertyValue('grid-template-columns');
    const columnCount = gridTemplateColumns.split(' ').length;

    const wrapper = document.createElement('li');
    wrapper.className = 'grid__item reviews-section-wrapper';
    
    // Set grid column to span full width
    wrapper.style.gridColumn = `1 / -1`;
    wrapper.style.width = '100%';
    wrapper.style.margin = '20px 0';

    // Inject Liquid-rendered HTML
    if (templateHeader) {
      const headerNode = document.createElement('div');
      headerNode.innerHTML = templateHeader.innerHTML;
      wrapper.appendChild(headerNode);
    }
    const reviewsNode = document.createElement('div');
    reviewsNode.innerHTML = template.innerHTML;
    wrapper.appendChild(reviewsNode);

    // Calculate insertion point - round up to next row
    const rowIndex = Math.ceil(afterIndex / columnCount) * columnCount;
    const targetIndex = Math.min(rowIndex - 1, productItems.length - 1);
    const target = productItems[targetIndex];
    
    target.parentNode.insertBefore(wrapper, target.nextSibling);
    
    // Initialize the reviews section
    this.initializeReviewsSection(wrapper);
  }

  static getReviewsSectionHTML() {
    const reviewsTemplate = document.getElementById("reviews-section-template");
    if (reviewsTemplate) {
      return reviewsTemplate.innerHTML;
    }

    return `
      <section class="rc2-reviews-section" style="width: 100%; padding: 40px 0; background-color: #FAF4F0;">
        <div class="rc2-container-wrapper">
          <div class="rc2-slider-component rc2-reviews-wrapper" id="rc2-reviews-container-inline">
            <div class="rc2-loading">
              <p>Loading reviews...</p>
            </div>
          </div>
          
          <div class="rc2-progress-nav" style="margin-top: 22px;">
            <div class="rc2-progress-wrapper">
              <div class="rc2-nav-arrows" style="display: flex; gap: 10px; justify-content: center;">
                <button class="rc2-nav-arrow rc2-prev-inline" aria-label="Previous reviews">
                  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style="width: 18px; height: 18px; stroke: #111; fill: none; stroke-width: 2;">
                    <path d="M15 18l-6-6 6-6" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
                <button class="rc2-nav-arrow rc2-next-inline" aria-label="Next reviews">
                  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style="width: 18px; height: 18px; stroke: #111; fill: none; stroke-width: 2;">
                    <path d="M9 18l6-6-6-6" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    `;
  }

  static initializeReviewsSection(wrapper) {
    const reviewsContainer = wrapper.querySelector(
      "#rc2-reviews-container-inline",
    );
    const loadingState = wrapper.querySelector(".rc2-loading");

    const apiUrl = wrapper.dataset.apiUrl || "https://api.zuvees.ae/reviews";
    const maxReviews = parseInt(wrapper.dataset.maxReviews || "8");

    InfiniteScroll.fetchAndRenderReviews(
      reviewsContainer,
      loadingState,
      apiUrl,
      maxReviews,
    );

    InfiniteScroll.initReviewsNavigation(wrapper);
  }

  static async fetchAndRenderReviews(
    container,
    loadingState,
    apiUrl,
    maxReviews,
  ) {
    try {
      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        mode: "cors",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const reviews = data.reviews || data;

      if (reviews.length > 0) {
        loadingState.style.display = "none";
        container.innerHTML = InfiniteScroll.renderReviews(
          reviews.slice(0, maxReviews),
        );
      }
    } catch (error) {
      console.error("Failed to load reviews:", error);
      loadingState.innerHTML = "<p>Unable to load reviews</p>";
    }
  }

  static renderReviews(reviews) {
    return reviews
      .map((review) => {
        const stars = InfiniteScroll.createStarRating(review.rating || 5);
        const date = review.reviewTime
          ? InfiniteScroll.formatRelativeDate(review.reviewTime)
          : "";
        const comment = review.comment || "";
        const authorName = review.authorName || "Anonymous";

        return `
        <div class="rc2-review-item" style="flex: 0 0 320px; min-width: 370px; background: #f6f6f6; border-radius: 8px; padding: 28px 26px; text-align: center;">
          ${stars}
          ${comment ? `<p class="rc2-review-text" style="font-size: 15px; line-height: 1.6; margin-top: 16px;">"${InfiniteScroll.escapeHtml(comment)}"</p>` : ""}
          <div class="rc2-review-meta" style="margin-top: 16px; display: flex; justify-content: center; gap: 8px;">
            <span class="rc2-author-name" style="font-size: 15px; font-weight: 700;">${InfiniteScroll.escapeHtml(authorName)}</span>
            ${date ? `<span class="rc2-review-date" style="font-size: 13px; color: #666;">${date}</span>` : ""}
          </div>
        </div>
      `;
      })
      .join("");
  }

  static createStarRating(rating) {
    const fullStars = Math.floor(rating);
    const decimal = rating - fullStars;
    const emptyStars = 5 - fullStars - (decimal > 0 ? 1 : 0);

    let stars =
      '<div class="rc2-star-rating" style="display: flex; justify-content: center; margin-bottom: 16px; gap: 2px;">';

    for (let i = 0; i < fullStars; i++) {
      stars +=
        '<span class="rc2-star full" style="font-size: 20px; color: #660033;">★</span>';
    }

    if (decimal > 0) {
      stars += `<span class="rc2-star partial" style="font-size: 20px;">★</span>`;
    }

    for (let i = 0; i < emptyStars; i++) {
      stars +=
        '<span class="rc2-star empty" style="font-size: 20px; color: #dcdce6;">★</span>';
    }

    stars += "</div>";
    return stars;
  }

  static formatRelativeDate(timestamp) {
    const now = new Date();
    const reviewDate = new Date(timestamp);
    const diffDays = Math.floor((now - reviewDate) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "today";
    if (diffDays === 1) return "a day ago";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  }

  static escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }

  static initReviewsNavigation(wrapper) {
    const container = wrapper.querySelector(".rc2-slider-component");
    const prevBtn = wrapper.querySelector(".rc2-prev-inline");
    const nextBtn = wrapper.querySelector(".rc2-next-inline");

    if (!container || !prevBtn || !nextBtn) return;

    prevBtn.addEventListener("click", () => {
      container.scrollBy({
        left: -container.offsetWidth * 0.8,
        behavior: "smooth",
      });
    });

    nextBtn.addEventListener("click", () => {
      container.scrollBy({
        left: container.offsetWidth * 0.8,
        behavior: "smooth",
      });
    });
  }
}

customElements.define("infinite-scroll", InfiniteScroll);